// src/pages/SettingsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { trackEvent } from '../analytics';
import { Link } from 'react-router-dom';

const API_BASE_URL = window.location.hostname === 'localhost' ? 'http://localhost:7071' : '';

// A reusable sub-component for managing lists of items
function ListManager({ title, listItems, onAddItem, onDeleteItem, inputPlaceholder, isLoading }) {
    const [newItem, setNewItem] = useState('');

    const handleAddClick = (e) => {
        e.preventDefault();
        if (newItem.trim() === '') return;
        onAddItem(newItem);
        setNewItem('');
    };

    return (
        <div className="list-manager">
            <h4>{title}</h4>
            <ul>
                {listItems && listItems.map(item => (
                    <li key={item.Id}>
                        <span>{item.ApplicationDisplayName || item.DomainName}</span>
                        <button className="button-delete" onClick={() => onDeleteItem(item.Id)} disabled={isLoading}>Delete</button>
                    </li>
                ))}
                {(!listItems || listItems.length === 0) && <li className="empty-list">No items configured.</li>}
            </ul>
            <div className="add-item-form">
                <input type="text" placeholder={inputPlaceholder} value={newItem} onChange={(e) => setNewItem(e.target.value)} disabled={isLoading} />
                <button onClick={handleAddClick} disabled={isLoading || newItem.trim() === ''}>Add</button>
            </div>
        </div>
    );
}

// The main SettingsPage component now receives the initial config as a prop
function SettingsPage({ initialConfig }) {
    const [workspaceId, setWorkspaceId] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');
    const [sensitiveApps, setSensitiveApps] = useState([]);
    const [approvedDomains, setApprovedDomains] = useState([]);
    const [isListLoading, setIsListLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // --- NEW STATE for the billing portal button ---
    const [isPortalLoading, setIsPortalLoading] = useState(false);

    const fetchLists = useCallback(async () => {
        setIsListLoading(true);
        try {
            const [appsRes, domainsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/customer/sensitive-apps`),
                fetch(`${API_BASE_URL}/api/customer/approved-domains`)
            ]);
            const appsData = appsRes.ok ? await appsRes.json() : [];
            const domainsData = domainsRes.ok ? await domainsRes.json() : [];
            setSensitiveApps(appsData);
            setApprovedDomains(domainsData);
        } catch (e) {
            setError(e.message);
        } finally {
            setIsListLoading(false);
        }
    }, []);

    useEffect(() => {
        if (initialConfig) {
            setWorkspaceId(initialConfig.LogAnalyticsWorkspaceId || '');
        }
        fetchLists();
    }, [initialConfig, fetchLists]);

    const handleUpdateWorkspaceId = async (event) => {
        event.preventDefault();
        setIsSaving(true);
        setError(null);
        setSaveMessage('');
        try {
            const response = await fetch(`${API_BASE_URL}/api/customer/settings`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ logAnalyticsWorkspaceId: workspaceId }),
            });
            const responseText = await response.text();
            if (!response.ok) throw new Error(responseText);
            setSaveMessage("Workspace ID saved successfully!");
            trackEvent('settings_save_workspace');
        } catch (e) {
            setError(`Failed to save settings: ${e.message}`);
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleAddItem = async (itemType, value) => {
        const endpoint = itemType === 'app' ? 'sensitive-apps' : 'approved-domains';
        const body = itemType === 'app' ? { appName: value } : { domainName: value };
        setIsListLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/customer/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!response.ok) throw new Error(await response.text());
            await fetchLists(); 
        } catch (e) {
            setError(`Failed to add item: ${e.message}`);
        } finally {
            setIsListLoading(false);
        }
    };

    const handleDeleteItem = async (itemType, id) => {
        if (!window.confirm("Are you sure you want to delete this item?")) return;
        const endpoint = itemType === 'app' ? 'sensitive-apps' : 'approved-domains';
        setIsListLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/customer/${endpoint}/${id}`, { method: 'DELETE' });
            if (!response.ok) throw new Error(await response.text());
            await fetchLists();
        } catch (e) {
            setError(`Failed to delete item: ${e.message}`);
        } finally {
            setIsListLoading(false);
        }
    };

    // --- NEW HANDLER for the billing portal ---
    const handleManageBilling = async () => {
        setIsPortalLoading(true);
        setError(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/create-portal-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // Send the current page URL as the return URL for Stripe
                body: JSON.stringify({ return_url: window.location.href })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to load billing portal.");
            }

            const data = await response.json();
            // Redirect the user to the Stripe Billing Portal
            trackEvent('billing_portal_open');
            window.location.href = data.url;

        } catch (e) {
            setError(e.message);
            setIsPortalLoading(false); // Stop loading only if there's an error
        }
    };
    // -----------------------------------------

    if (!initialConfig) return <p>Loading settings...</p>;

    return (
        <div className="settings-page">
            <h2>Your Settings</h2>
            {error && <p className="error">{error}</p>}
            
            <form onSubmit={handleUpdateWorkspaceId} className="settings-form main-settings-form">
                <h4>Primary Configuration</h4>
                <div className="form-field">
                    <label>Organization Name:</label>
                    <input type="text" value={initialConfig.OrganizationName || ''} disabled />
                </div>
                <div className="form-field">
                    <label>Your Azure Tenant ID:</label>
                    <input type="text" value={initialConfig.AzureTenantId || ''} disabled />
                </div>
                <div className="form-field">
                    <label htmlFor="workspaceId">Log Analytics Workspace ID:</label>
                    <input id="workspaceId" type="text" value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)} required />
                </div>
                {saveMessage && <p className="success-message">{saveMessage}</p>}
                <button type="submit" disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Workspace ID'}
                </button>
            </form>

            {/* --- NEW SECTION for Billing Management --- */}
            <hr />
            <h3>Subscription & Billing</h3>
            <div className="settings-form">
                <p>Manage your subscription, view invoices, and update payment methods.</p>
                <button type="button" onClick={handleManageBilling} disabled={isPortalLoading}>
                    {isPortalLoading ? 'Loading Portal...' : 'Manage Subscription'}
                </button>
            </div>
            {/* ----------------------------------------- */}
            
            <hr />
            <h3>Compliance Rule Settings</h3>
            <div className="settings-lists-container">
                <ListManager title="Manage Sensitive Applications" listItems={sensitiveApps} onAddItem={(appName) => handleAddItem('app', appName)} onDeleteItem={(appId) => handleDeleteItem('app', appId)} inputPlaceholder="Add Application Name" isLoading={isListLoading} />
                <ListManager title="Manage Approved Sharing Domains" listItems={approvedDomains} onAddItem={(domainName) => handleAddItem('domain', domainName)} onDeleteItem={(domainId) => handleDeleteItem('domain', domainId)} inputPlaceholder="Add Approved Domain" isLoading={isListLoading} />
            </div>

            <hr />
            <h3>Help & Legal</h3>
            <div className="settings-legal">
                <p className="settings-legal-text">Looking for our policies or need to reach us?</p>
                <ul className="settings-legal-links">
                    <li><Link to="/privacy">Privacy Policy</Link></li>
                    <li><Link to="/terms">Terms & Conditions</Link></li>
                    <li><Link to="/contact">Contact Us</Link></li>
                </ul>
            </div>
        </div>
    );
}

export default SettingsPage;
